import clsx from 'clsx';

import {usePagination} from '@/hooks';

type profileDataActionT = {
  title: string;
  data: {src: string; name: string}[];
  className?: string;
  dataActions?: {icon: string; handler: () => void}[];
  onViewAll?: () => void;
};

const itemsPerPage = 3;

/**
 * This component is used to render data with action buttons.
 * @param {string} title - The title of the data.
 * @param {object[]} data - The data to be rendered.
 * @param {string} [className] - The class name of the component.
 * @param {object[]} [dataActions] - The actions to be rendered.
 * @param {function} [onViewAll] - The function to be called when view all is clicked.
 */
export const ProfileDetailsDataItemActionComponent: React.FC<profileDataActionT> = ({
  title,
  data,
  className,
  onViewAll,
  dataActions,
}: profileDataActionT) => {
  const {currentPage, totalPages, currentData, handlePrev, handleNext, goToPage} = usePagination(data, itemsPerPage);

  return (
    <div className={clsx('box h-min', className)}>
      <div className="box-header flex justify-between">
        <div className="box-title">{title}</div>
        {onViewAll && (
          <button
            type="button"
            className="ti-btn ti-btn-success !px-2 !py-1 !text-[0.75rem] !font-medium"
            onClick={onViewAll}
          >
            View All
          </button>
        )}
      </div>
      <div className="box-body max-h-[300px] flex-grow-0 overflow-y-auto">
        <ul className="list-group">
          {currentData.map((item, index) => (
            <li className="list-group-item" key={index}>
              <div className="flex items-center justify-between">
                <div className="flex items-center font-semibold">
                  <span className="avatar avatar-xs me-2">
                    <img src={item.src} alt="" />
                  </span>
                  {item.name}
                </div>
                <div className="items-fled-end !mb-0 flex !gap-2">
                  {dataActions &&
                    dataActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={action.handler}
                        aria-label="button"
                        type="button"
                        className="ti-btn ti-btn-sm ti-btn-primary !mb-0"
                      >
                        <i className={`${action.icon}`}></i>
                      </button>
                    ))}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="box-footer">
        <div className="items-center sm:flex">
          <div className="m-auto">
            <nav aria-label="Page navigation" className="pagination-style-4">
              <ul className="ti-pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handlePrev}>
                    Prev
                  </button>
                </li>
                {[...Array(totalPages).keys()].map(page => (
                  <li className={`page-item ${currentPage === page + 1 ? 'active' : ''}`} key={page}>
                    <button
                      className={`page-link ${currentPage === page + 1 ? 'active' : ''}`}
                      onClick={() => goToPage(page + 1)}
                    >
                      {page + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNext}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};
